import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { NAV, SITE } from "../content/site";

function Wordmark() {
  return (
    <Link to="/" className="flex items-baseline gap-2" data-testid="nav-logo">
      <span className="font-heading text-xl font-semibold tracking-tight text-brand-navy">
        GPS<span className="text-primary">SmartLabels</span>
      </span>
      <a
        href="https://goandtrack.com/"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="nav-goandtrack-link"
        className="hidden text-[11px] font-mono lowercase tracking-wider text-brand-slate transition-colors duration-150 hover:text-primary sm:inline"
      >
        powered by goandtrack.com
      </a>
    </Link>
  );
}

export function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <div className="flex min-h-screen flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-primary focus:px-4 focus:py-2 focus:text-white"
        data-testid="skip-to-content"
      >
        Skip to content
      </a>
      <header className="sticky top-0 z-40 border-b border-border bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-8">
          <Wordmark />
          <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary" data-testid="desktop-nav">
            {NAV.slice(0, 7).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={({ isActive }) =>
                  `rounded-sm px-3 py-2 text-sm font-medium transition-colors duration-150 ${
                    isActive ? "text-primary" : "text-brand-slate hover:text-brand-navy"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/request-a-pilot"
              data-testid="nav-request-pilot-cta"
              className="ml-3 inline-flex items-center gap-1.5 rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-blue-700"
            >
              Request a Pilot <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </nav>
          <button
            className="rounded-sm p-2 lg:hidden"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-label="Toggle navigation menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {open && (
          <nav className="border-t border-border bg-white px-4 py-4 lg:hidden" aria-label="Mobile" data-testid="mobile-nav">
            <ul className="space-y-1">
              {NAV.map((n) => (
                <li key={n.to}>
                  <NavLink
                    to={n.to}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={({ isActive }) =>
                      `block rounded-sm px-3 py-2.5 text-sm font-medium ${
                        isActive ? "bg-brand-surface text-primary" : "text-brand-slate"
                      }`
                    }
                  >
                    {n.label}
                  </NavLink>
                </li>
              ))}
              <li className="pt-2">
                <Link
                  to="/request-a-pilot"
                  data-testid="mobile-nav-request-pilot-cta"
                  className="block rounded-sm bg-primary px-4 py-3 text-center text-sm font-semibold text-white"
                >
                  Request a Pilot
                </Link>
              </li>
            </ul>
          </nav>
        )}
      </header>

      <main id="main" className="flex-1">
        {children}
      </main>

      <footer className="border-t border-border bg-brand-surface" data-testid="site-footer">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-8 md:grid-cols-4">
          <div className="md:col-span-2">
            <Wordmark />
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-brand-slate">{SITE.tagline}</p>
            <p className="mt-4 text-xs text-brand-slate">
              Contact: <a href={`mailto:${SITE.email}`} className="text-primary underline-offset-2 hover:underline" data-testid="footer-email">{SITE.email}</a>
              <span className="block mt-1">[Registered address and company number pending approval]</span>
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-brand-navy">Products</h3>
            <ul className="mt-3 space-y-2 text-sm text-brand-slate">
              <li><Link to="/gnts1-bluetooth-smart-sticker" className="hover:text-primary" data-testid="footer-gnts1">GNTS1 Bluetooth Smart Sticker</Link></li>
              <li><Link to="/ll100-4g-smart-label" className="hover:text-primary" data-testid="footer-ll100">LL100 4G Smart Label</Link></li>
              <li><Link to="/compare-smart-labels" className="hover:text-primary" data-testid="footer-compare">Compare labels</Link></li>
              <li><Link to="/smart-label-pilot" className="hover:text-primary" data-testid="footer-pilot">Pilot programme</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-brand-navy">Company &amp; Legal</h3>
            <ul className="mt-3 space-y-2 text-sm text-brand-slate">
              <li><Link to="/about" className="hover:text-primary" data-testid="footer-about">About</Link></li>
              <li><Link to="/insights" className="hover:text-primary" data-testid="footer-insights">Insights</Link></li>
              <li><Link to="/privacy" className="hover:text-primary" data-testid="footer-privacy">Privacy</Link></li>
              <li><Link to="/cookies" className="hover:text-primary" data-testid="footer-cookies">Cookies</Link></li>
              <li><Link to="/terms" className="hover:text-primary" data-testid="footer-terms">Terms</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border">
          <p className="mx-auto max-w-7xl px-4 py-5 text-xs text-brand-slate sm:px-8">
            © {new Date().getFullYear()} {SITE.brand}. Specifications shown in [brackets] are placeholders pending verification against approved datasheets.
          </p>
        </div>
      </footer>
    </div>
  );
}
